import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../UserContext';

const DEFAULT_STATE = {
  podId: null,
  podRole: null,
  pod: null,
  isAdmin: false,
  isMember: false,
  hasPod: false,
  isLoading: true,
};

export function usePodMembership() {
  const { currentUser, isAuthenticated } = useAuth();
  const [state, setState] = useState(DEFAULT_STATE);

  useEffect(() => {
    if (!isAuthenticated || !currentUser?.uid) {
      setState({ ...DEFAULT_STATE, isLoading: false });
      return () => {};
    }

    const userRef = doc(db, 'users', currentUser.uid);
    let unsubscribePod = null;

    const unsubscribeUser = onSnapshot(
      userRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setState({ ...DEFAULT_STATE, isLoading: false });
          if (unsubscribePod) {
            unsubscribePod();
            unsubscribePod = null;
          }
          return;
        }

        const userData = snapshot.data() || {};
        const nextPodId = userData?.podId || null;
        const nextPodRole = userData?.podRole || null;

        if (!nextPodId) {
          setState({
            podId: null,
            podRole: null,
            pod: null,
            isAdmin: false,
            isMember: false,
            hasPod: false,
            isLoading: false,
          });
          if (unsubscribePod) {
            unsubscribePod();
            unsubscribePod = null;
          }
          return;
        }

        if (unsubscribePod) {
          unsubscribePod();
          unsubscribePod = null;
        }

        const podRef = doc(db, 'pods', nextPodId);
        unsubscribePod = onSnapshot(
          podRef,
          (podSnapshot) => {
            const podData = podSnapshot.exists()
              ? { id: podSnapshot.id, ...podSnapshot.data() }
              : null;

            setState({
              podId: nextPodId,
              podRole: nextPodRole,
              pod: podData,
              isAdmin: nextPodRole === 'admin',
              isMember: nextPodRole === 'member',
              hasPod: true,
              isLoading: false,
            });
          },
          (podError) => {
            console.error('Failed to load pod data:', podError);
            setState({
              podId: nextPodId,
              podRole: nextPodRole,
              pod: null,
              isAdmin: nextPodRole === 'admin',
              isMember: nextPodRole === 'member',
              hasPod: true,
              isLoading: false,
            });
          }
        );
      },
      (error) => {
        console.error('Failed to load pod membership:', error);
        setState({ ...DEFAULT_STATE, isLoading: false });
      }
    );

    return () => {
      unsubscribeUser();
      if (unsubscribePod) {
        unsubscribePod();
      }
    };
  }, [currentUser?.uid, isAuthenticated]);

  return state;
}

const DEFAULT_ORG_ACCESS = {
  orgId: null,
  orgRoles: [],
  isOrgAdmin: false,
  isBillingAdmin: false,
  canAccessAdmin: false,
  isLoading: true,
};

/**
 * Org access for nav-gating ONLY. Reads the caller's OWN user doc for `orgId`
 * and `orgRoles` (which Firestore rules allow a user to read about themselves).
 * It deliberately does NOT read the `organizations/{orgId}` doc: that holds the
 * pooled credit balance, which is visible to Org / Billing Admins ONLY and is
 * served exclusively through the role-gated admin endpoints. This hook just
 * decides whether to show the Admin-panel entry point.
 */
export function useOrgAccess() {
  const { currentUser, isAuthenticated } = useAuth();
  const [state, setState] = useState(DEFAULT_ORG_ACCESS);

  useEffect(() => {
    if (!isAuthenticated || !currentUser?.uid) {
      setState({ ...DEFAULT_ORG_ACCESS, isLoading: false });
      return () => {};
    }

    const userRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(
      userRef,
      (snapshot) => {
        const data = snapshot.exists() ? (snapshot.data() || {}) : {};
        const orgId = data?.orgId || null;
        const orgRoles = Array.isArray(data?.orgRoles) ? data.orgRoles : [];
        const isOrgAdmin = orgRoles.includes('orgAdmin');
        const isBillingAdmin = orgRoles.includes('billingAdmin');
        setState({
          orgId,
          orgRoles,
          isOrgAdmin,
          isBillingAdmin,
          canAccessAdmin: !!orgId && (isOrgAdmin || isBillingAdmin),
          isLoading: false,
        });
      },
      (error) => {
        console.error('Failed to load org access:', error);
        setState({ ...DEFAULT_ORG_ACCESS, isLoading: false });
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid, isAuthenticated]);

  return state;
}
